// `calculateShowContactDetails` / `calculateDisplayEmailAndPhone`
// (`extension-alert-client-contact-snippet.component.ng1.ts:1267`): what the contact snippet
// shows for a contact's email and phone, and whether each is masked.
//
// ⚠️ THE PHONE IS `phoneNumbers.join(", ")`, NOT A SINGLE FIELD. The port read a singular
// `phoneNumber` here, which a Vincere contact on the dev tenant does not carry, so its profile
// dropped the phone line altogether (measured 28 Oct against the re-captured reference).

/** `ClientContactDetailsSource` — a numeric enum on the wire; only ATS (0) matters here. */
export const ClientContactDetailsSource = { ATS: 0 } as const

export interface ContactDetailsInput {
  email?: string | null
  phoneNumbers?: readonly string[] | null
  emailSource?: number | null
  phoneNumberSource?: number | null
  /** Epoch ms of the user's last "Get phone and email". */
  lastTimeUserRequestedContactDetails?: number | null
}

export interface ContactDetailsDisplay {
  email: string | null
  emailHidden: boolean
  phone: string | null
  phoneHidden: boolean
  /** The "Added by Woo" tag next to each, when the value is shown and did not come from the ATS. */
  emailAddedByWoo: boolean
  phoneAddedByWoo: boolean
}

const DAY_MS = 1000 * 60 * 60 * 24

/**
 * @param hideEnabled `featureFlags.HIDE_CLIENT_CONTACT_CONTACT_DETAILS`.
 * @param revealed    the user has just requested the details (`calculateDisplayEmailAndPhone(false)`).
 */
export function contactDetailsDisplay(
  contact: ContactDetailsInput,
  hideEnabled: boolean,
  now: number,
  revealed = false,
): ContactDetailsDisplay {
  const phones = contact.phoneNumbers !== undefined && contact.phoneNumbers !== null && contact.phoneNumbers.length > 0
    ? contact.phoneNumbers.join(', ')
    : null
  const emailByWoo = contact.emailSource !== undefined && contact.emailSource !== null && contact.emailSource !== ClientContactDetailsSource.ATS
  const phoneByWoo =
    contact.phoneNumberSource !== undefined && contact.phoneNumberSource !== null && contact.phoneNumberSource !== ClientContactDetailsSource.ATS
  const email = contact.email === undefined || contact.email === '' ? null : contact.email

  if (!hideEnabled) {
    return {
      email,
      emailHidden: false,
      phone: phones,
      phoneHidden: false,
      emailAddedByWoo: email !== null && emailByWoo,
      phoneAddedByWoo: phones !== null && phoneByWoo,
    }
  }

  // On init the details stay masked unless Woo added one of them AND the user asked for them in
  // the last day; after a request they are shown.
  const requestedRecently =
    contact.lastTimeUserRequestedContactDetails !== undefined &&
    contact.lastTimeUserRequestedContactDetails !== null &&
    contact.lastTimeUserRequestedContactDetails !== 0 &&
    now - contact.lastTimeUserRequestedContactDetails < DAY_MS
  const hide = revealed ? false : !((emailByWoo || phoneByWoo) && requestedRecently)

  let shownEmail: string | null
  let emailHidden: boolean
  if (emailByWoo) {
    shownEmail = hide ? censoredEmail(email ?? '') : email
    emailHidden = hide
  } else if (contact.emailSource === ClientContactDetailsSource.ATS && email !== null) {
    // ATS-sourced details are the customer's own data and are never masked; a null source stays
    // masked, because its provenance is unknown.
    shownEmail = email
    emailHidden = false
  } else {
    shownEmail = '****@**.**'
    emailHidden = true
  }

  let shownPhone: string | null
  let phoneHidden: boolean
  if (phoneByWoo) {
    shownPhone = hide ? censoredPhone(contact.phoneNumbers ?? []) : phones
    phoneHidden = hide
  } else if (contact.phoneNumberSource === ClientContactDetailsSource.ATS && phones !== null) {
    shownPhone = phones
    phoneHidden = false
  } else {
    shownPhone = '***-****'
    phoneHidden = true
  }

  return {
    email: shownEmail,
    emailHidden,
    phone: shownPhone,
    phoneHidden,
    emailAddedByWoo: !emailHidden && emailByWoo,
    phoneAddedByWoo: !phoneHidden && phoneByWoo,
  }
}

/** `getCensoredEmail` — the first two characters and the top-level domain survive. */
export function censoredEmail(email: string): string {
  const firstTwo = email.substring(0, 2)
  const parts = email.split('@')
  if (parts.length < 2) return `${firstTwo}****@**.**`
  const domain = parts[1] ?? ''
  const lastDot = domain.lastIndexOf('.')
  if (lastDot < 0) return `${firstTwo}****@**.**`
  return `${firstTwo}****@**.${domain.slice(lastDot + 1)}`
}

/** `getCensoredPhone` — the last two digits of each number survive. */
export function censoredPhone(phoneNumbers: readonly string[]): string {
  return phoneNumbers.map((phone) => `***-**${phone.slice(-2)}`).join(', ')
}
